import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import React, { ChangeEvent, useEffect, useState } from 'react';

import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import { deleteDispositionAttribute, getDispositionAttributes, upsertDispositionAttribute } from '../api';
import { DispositionAttribute } from '../domain';
import AttributeCard from './AttributeCard';
import CreateEditAttributeModal from './CreateEditAttributeModal';

interface Query {
  search: string;
}

interface Error {
  text: string;
  subText: string;
}

const DispositionAttributesList = () => {
  const { pushNotification } = useNotification();
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editableDispositionAttribute, setEditableDispositionAttribute] = useState<DispositionAttribute | undefined>(
    undefined,
  );
  const [submittingData, setSubmittingData] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [dispositionAttributes, setDispositionAttributes] = useState<DispositionAttribute[]>([]);
  const [query, setQuery] = useState<Query>({
    search: '',
  });

  useEffect(() => {
    (async () => {
      try {
        setDispositionAttributes(await getDispositionAttributes());
      } catch (e) {
        handleError(e);
      } finally {
        setPageLoaded(true);
      }
    })();
  }, []);

  const handleError = (e: any) => {
    if (e instanceof APIError) {
      setError({ text: 'Unable to request data from backend', subText: e.message });
    }

    if (e instanceof UnsupportedStructureError) {
      setError({ text: 'Data from backend Invalid', subText: 'Unable to decode response' });
    }
  };

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const openEditModal = (item: DispositionAttribute) => () => {
    setEditableDispositionAttribute(item);
  };

  const closeModal = () => {
    setCreateModalOpen(false);
    setEditableDispositionAttribute(undefined);
  };

  const onQueryChange = (e: ChangeEvent<any>) => {
    const { name, value } = e.target;
    setQuery((prev) => ({ ...prev, [name]: value }));
  };

  const upsertAttribute = async (isEdit: boolean, data: DispositionAttribute) => {
    setSubmittingData(true);

    try {
      await upsertDispositionAttribute(isEdit, data);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);

      // Modal catches error to prevent form reset on create failure
      return Promise.reject();
    } finally {
      setSubmittingData(false);
    }

    pushNotification('success', `Created disposition attribute ${data.attribute}`);

    try {
      setDispositionAttributes(await getDispositionAttributes());
    } catch (e) {
      handleError(e);
    } finally {
      closeModal();
    }
  };

  const deleteAttribute = (attribute: string) => async () => {
    setSubmittingData(true);

    try {
      await deleteDispositionAttribute(attribute);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);

      // Modal catches error to prevent form reset on create failure
      return Promise.reject();
    } finally {
      setSubmittingData(false);
    }

    pushNotification('success', `deleted disposition attribute ${attribute}`);

    try {
      setDispositionAttributes(await getDispositionAttributes());
    } catch (e) {
      handleError(e);
    } finally {
      closeModal();
    }
  };

  const displayList = dispositionAttributes
    // Search Fuzzy matching
    .filter((item: DispositionAttribute) => {
      if (query.search) {
        const searchQuery = query.search.toLowerCase();
        return item.attribute.toLowerCase().includes(searchQuery);
      }

      return true;
    })
    // Component generation
    .map((item: DispositionAttribute, index: number) => (
      <AttributeCard
        key={index}
        dispositionAttribute={item}
        onEdit={openEditModal(item)}
        onDelete={deleteAttribute(item.attribute)}
      />
    ));

  // If any filter property is set and displayList is empty we assume no relevant search results
  const noSearchOrFilterResults = query.search && displayList.length === 0;
  const noResults = dispositionAttributes.length === 0;

  const errorDisplay = error ? <EmptyState type='error' text={error.text} subText={error.subText} /> : null;

  return (
    <AsyncLoader isLoading={!pageLoaded} error={errorDisplay}>
      {noResults && (
        <EmptyState
          type='no-items-3'
          text='No disposition attributes currently exist'
          subText='Create a disposition attribute by clicking the button below'
          action={openCreateModal}
          actionText='Create Attribute'
        />
      )}

      {!noResults && (
        <Grid container spacing={1} alignContent='center'>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant='outlined'
              label='Search'
              id='search'
              name='search'
              defaultValue={query.search}
              onChange={onQueryChange}
            />
          </Grid>

          <Hidden smDown>
            <Grid item md={5}></Grid>
          </Hidden>

          <Grid style={{ display: 'flex', alignItems: 'center' }} item xs={12} md={3}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}
              onClick={openCreateModal}>
              Create Attribute
            </Button>
          </Grid>

          <Grid item xs={12}>
            {!noSearchOrFilterResults && <List>{displayList}</List>}
            {noSearchOrFilterResults && (
              <EmptyState
                type='no-records-found'
                text='No disposition attributes found matching your search criteria'
                subText='Try alternate words or selections.'
              />
            )}
          </Grid>
        </Grid>
      )}

      <CreateEditAttributeModal
        open={createModalOpen || Boolean(editableDispositionAttribute)}
        submitting={submittingData}
        dispositionAttribute={editableDispositionAttribute}
        onClose={closeModal}
        onAccept={upsertAttribute}
      />
    </AsyncLoader>
  );
};

export default DispositionAttributesList;
